<template>
    <div class="tel-with-confirm">
        <div v-if="label && !current" :data-voicetext="label" class="tel-with-confirm__label" :class="{ focused: isFocused }">
            {{ label }}
        </div>
        <vue-tel-input
            v-if="!current"
            v-model="innerValue"
            :data-voicetext="innerValue"
            v-bind="telInputProps"
            @input="updateInnerValue"
            @focus="changeClass"
            @blur="changeClass"
            @country-changed="setCountryCode"
        >
            <template v-slot:arrow-icon>
                <div class="arrow"></div>
            </template>
            <template v-slot:icon-right>
                <span
                    v-if="regTel === `+${countryCode}${innerValue}` || isTelConfirmed(`+${countryCode}${innerValue}`)"
                    slot="phone-confirmed"
                    class="model-input__phone-confirmed"
                >
                    Подтвержден
                </span>
                <RsvButton
                    v-else
                    reset
                    :disabled="!isInnerValueValid"
                    slot="button"
                    :text="isMobile ? 'Отправить смс' : 'Отправить смс с кодом'"
                    class="model-input__sms-check-button"
                    @click="requestNewCode(`+${countryCode}${innerValue}`)"
                />
            </template>
        </vue-tel-input>
        <p
            v-if="!current && !isTelConfirmed(`+${countryCode}${innerValue}`)"
            slot="need-confirm-message"
            class="model-input__need-confirm-message confirm-warning"
        >
            <template v-if="secondsUntilNextCodeRequestAvailable > 0 || errorMessage || getTelErrorMsg">
                <template v-if="errorMessage || getTelErrorMsg">
                    {{ (errorMessage ? `${errorMessage}.` : '') || getTelErrorMsg }}
                </template>
                <template v-if="secondsUntilNextCodeRequestAvailable > 0">
                    Отправка будет доступна через {{ secondsUntilNextCodeRequestAvailable | countdownTimer }}
                </template>
            </template>
            <template v-else>Необходимо подтвердить номер телефона</template>
        </p>

        <SmsCheckBlock
            v-if="current"
            :errorMessage="errorMessage"
            :tel="current.tel"
            :nextCodeRequestAt="nextCodeRequestAt"
            :secondsUntilNextCodeRequestAvailable="secondsUntilNextCodeRequestAvailable"
            @nextCodeRequest="requestNewCode(`+${countryCode}${innerValue}`)"
            @submit="confirmTel"
            @cancel="resetForm"
        />
    </div>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters, mapState } from 'vuex';

import SmsCheckBlock from '../UI/SmsCheckBlock/SmsCheckBlock';
import RsvButton from '../UI/RsvButton/RsvButton';

const telConfirmState = ['errorMessage', 'nextCodeRequestAt', 'current', 'confirmedTel'];

const telConfirmActions = [
    'loadConfirmation',
    'requestNewCode',
    'applyConfirmationCode',
    'setConfirmedTel',
    'cancelConfirmation',
    'resetErrorMessage',
];

const telConfirmGetters = ['isTelConfirmed'];

const getters = ['isMobile', 'getTelErrorMsg'];

export default {
    name: 'TelWithConfirm',
    props: {
        isConfirmed: Boolean,
        tel: String,
        label: String,
        regTel: String,
    },
    data: () => ({
        telInputProps: {
            autoFormat: false,
            preferredCountries: ['RU', 'KZ', 'BY'],
            defaultCountry: 'RU',
            autoDefaultCountry: false,
            mode: 'national',
            styleClasses: ['tel-input-custom', 'mt-input'],
            dropdownOptions: {
                showFlags: true,
                showDialCodeInList: true,
                width: '390px',
                showDialCodeInSelection: true,
            },
            inputOptions: {
                type: 'tel',
                autocomplete: 'off',
                showDialCode: false,
                maxlength: 13,
            },
        },
        isFocused: false,
        innerValue: '',
        countryCode: '',
        secondsUntilNextCodeRequestAvailable: 0,
    }),
    methods: {
        ...mapActions('telConfirmation', telConfirmActions),
        startTimer() {
            this.stopTimer();
            this.timer = window.setInterval(() => {
                this.calcSecondsUntilNextCodeRequestAvailable();
            }, 1000);
        },
        changeClass() {
            this.isFocused = !this.isFocused;
        },
        stopTimer() {
            if (this.timer) {
                window.clearInterval(this.timer);
            }
        },
        calcSecondsUntilNextCodeRequestAvailable() {
            if (this.nextCodeRequestAt === null) return;
            this.secondsUntilNextCodeRequestAvailable = Math.max(0, this.nextCodeRequestAt.diff(moment(), 'seconds'));
        },
        async confirmTel($event) {
            let tel = this.current.tel;
            await this.applyConfirmationCode($event);
            this.innerValue = tel.replace(`+${this.countryCode}`, '');
            this.$emit('change', {
                value: tel,
                isConfirmed: this.isTelConfirmed(tel),
            });
        },
        resetForm() {
            this.cancelConfirmation();
            this.innerValue = this.tel.replace(`+${this.countryCode}`, '');
        },
        updateInnerValue(v) {
            let fullValue;
            if (v.includes(`+${this.countryCode}`)) {
                fullValue = v;
                this.innerValue = v.replace(`+${this.countryCode}`, '');
            } else {
                fullValue = `+${this.countryCode}${this.innerValue}`;
                this.innerValue = v;
            }
            if (!this.innerValue) {
                fullValue = '';
            }
            this.$emit('change', {
                value: fullValue,
                isConfirmed: this.isTelConfirmed(fullValue),
            });
        },
        setCountryCode(v) {
            this.countryCode = v.dialCode;
            if (this.countryCode === '7') {
                this.telInputProps.inputOptions.maxlength = 10;
            } else {
                this.telInputProps.inputOptions.maxlength = 13;
            }
            if (this.innerValue) {
                this.innerValue = this.innerValue.replace(`+${v.dialCode}`, '');
            }
            let fullValue = `+${this.countryCode}${this.innerValue}`;
            if (!this.innerValue) {
                fullValue = '';
            }
            this.$emit('change', {
                value: fullValue,
                isConfirmed: this.isTelConfirmed(fullValue),
            });
        },
    },
    computed: {
        ...mapState('telConfirmation', telConfirmState),
        ...mapGetters('telConfirmation', telConfirmGetters),
        ...mapGetters(getters),
        isInnerValueValid() {
            return this.innerValue && this.innerValue.length >= 10;
        },
    },
    watch: {
        secondsUntilNextCodeRequestAvailable(time) {
            if (time > 0) {
                this.startTimer();
            } else {
                this.stopTimer();
            }
        },
        tel() {
            if (this.tel && this.countryCode) {
                this.innerValue = this.tel.replace(`+${this.countryCode}`, '');
                if (this.isConfirmed) {
                    this.setConfirmedTel(this.tel);
                }
            } else {
                this.innerValue = this.tel;
                if (this.isConfirmed) {
                    this.setConfirmedTel(this.tel);
                }
            }

            this.calcSecondsUntilNextCodeRequestAvailable();
        },
        nextCodeRequestAt() {
            this.calcSecondsUntilNextCodeRequestAvailable();
        },
        innerValue() {
            this.resetErrorMessage();
        },
    },
    components: {
        SmsCheckBlock,
        RsvButton,
    },
    created() {
        this.loadConfirmation();
    },
    mounted() {
        this.calcSecondsUntilNextCodeRequestAvailable();
    },
    destroyed() {
        this.stopTimer();
    },
};
</script>

<style lang="scss" scoped>
@import '~@/styles/rem';

.tel-with-confirm {
    position: relative;
    margin-bottom: 0;

    @include on-desktop {
        margin-bottom: 0;
    }

    &__label {
        color: #98a0a9;
        position: absolute;
        background: white;
        font-weight: 600;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        transition: 0.1s;
        pointer-events: none;
        white-space: nowrap;
        padding-left: mrem(6);
        padding-right: mrem(6);
        font-size: mrem(13);
        top: mrem(10);
        left: mrem(10);

        @include on-desktop {
            padding-left: rem(6);
            padding-right: rem(6);
            font-size: rem(12);
            top: rem(10);
            left: rem(10);
        }
    }
}
.timer {
    display: inline-block;
    margin-left: 1px;
}
.model-input__phone-confirmed {
    position: relative;
    font-weight: 600;
    background: #c2edc2;
    border: none;
    color: #2a2626;
    width: mrem(142);
    height: mrem(32);
    font-size: mrem(12);
    line-height: mrem(24);
    padding: mrem(4) mrem(16) mrem(4) mrem(36);
    border-radius: mrem(40);

    &::before {
        content: '';
        position: absolute;
        width: mrem(16);
        height: mrem(16);
        top: mrem(7);
        left: mrem(17);
        background: url('../../assets/images/complete.svg') no-repeat;
        background-size: mrem(16) mrem(16);

        @include on-desktop {
            background-size: rem(16) rem(16);
            width: rem(16);
            height: rem(16);
            top: rem(7);
            left: rem(17);
        }
    }

    @include on-desktop {
        width: rem(142);
        height: rem(32);
        font-size: rem(12);
        line-height: rem(24);
        padding: rem(4) rem(16) rem(4) rem(36);
        border-radius: rem(40);
    }
}

.model-input__sms-check-button {
    font-family: 'Montserrat';
    font-style: normal;
    z-index: inherit;
    font-weight: 600;
    text-align: center;
    color: #2a2626;
    background: #e2e2e2;
    font-size: mrem(12) !important;
    line-height: mrem(24);
    border-radius: mrem(40);
    height: mrem(32);
    border: mrem(1) solid #e2e2e2;
    min-width: mrem(122);

    @include on-desktop {
        font-size: rem(12) !important;
        line-height: rem(24);
        border-radius: rem(40);
        height: rem(32);
        border: rem(1) solid #e2e2e2;
        min-width: rem(208);
    }
}

.tel-with-confirm ::v-deep .vue-tel-input.tel-input-custom {
    display: flex;
    align-items: center;
    font-family: 'Proxima Nova', sans-serif;
    font-style: normal;
    font-weight: normal;
    line-height: rem(24);
    margin: mrem(16) mrem(0);
    padding: mrem(5) mrem(6) mrem(5) mrem(13);
    border-radius: mrem(10) !important;
    border: mrem(1) solid #98a0a9 !important;

    @include on-desktop {
        line-height: rem(24);
        margin: rem(16) rem(0);
        padding: rem(6.5) rem(6) rem(6.5) rem(13);
        border-radius: rem(10) !important;
        border: rem(1) solid #98a0a9 !important;
        max-width: rem(471.41);
    }

    .vti__dropdown-list {
        z-index: 999999;
        font-size: mrem(18);

        @include on-desktop {
            font-size: rem(18);
        }
    }

    .vti__dropdown {
        padding-left: mrem(0);
        padding-bottom: mrem(1);
        padding-top: mrem(4);

        @include on-desktop {
            padding-left: rem(0);
            padding-top: rem(4);
            padding-right: rem(7);
        }
    }

    .vti__dropdown-item {
        padding: mrem(10) mrem(15);

        @include on-desktop {
            padding: rem(4) rem(15);
        }
    }
    .vti__flag {
        height: mrem(14);
        width: mrem(18);
        margin-left: mrem(0);
        background-size: 5630px mrem(15);

        @include on-desktop {
            height: rem(14);
            width: rem(20);
            margin-left: rem(0);
            margin-right: rem(5);
            background-size: 5630px rem(15);
        }
    }

    input {
        font-size: mrem(16);
        padding-bottom: 0;
        font-family: 'Proxima Nova', sans-serif;

        @include on-desktop {
            padding-bottom: rem(5);
            padding-left: rem(7);
            font-size: rem(16);
        }
    }

    strong {
        font-weight: 500;
        line-height: mrem(20);

        @include on-desktop {
            line-height: rem(20);
        }
    }

    .vti__country-code {
        font-size: mrem(16);
        font-family: 'Proxima Nova', sans-serif;

        @include on-desktop {
            font-size: rem(16);
        }
    }

    &:focus-within {
        & + .tel-with-confirm__label {
            color: #4663ab !important;
        }

        box-shadow: none !important;
        border-color: #4663ab !important;
    }
}

.confirm-warning {
    position: relative;
    font-style: normal;
    font-weight: normal;
    color: #4663ab;
    text-align: right;
    margin: 0;
    font-size: mrem(13);
    line-height: mrem(24);
    right: 0;
    bottom: mrem(13);

    @include on-desktop {
        font-size: rem(13);
        line-height: rem(24);
        bottom: rem(15);
    }
}
.arrow {
    background: url('../../assets/images/tel-input-arrow.svg') no-repeat;
    width: mrem(12);
    height: mrem(6);
    margin-left: mrem(5);
    background-size: mrem(12) mrem(6);

    @include on-desktop {
        background-size: rem(12) rem(6);
        width: rem(12);
        height: rem(6);
        margin-left: rem(5);
    }
}
.focused {
    color: #4663ab;
}
</style>
